/**
 * Auto-apply an extra discount code on first subscription orders.
 * Subscribe & save is selling-plan pricing; this stacks a Shopify code on top.
 * Eligibility for "first subscription" is enforced by the Admin discount
 * (once per customer + subscription purchase type), with a theme-side skip
 * for logged-in customers who already have a subscription order.
 */
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const CART_SECTION_ID = 'cart-drawer';

function skipKey(code) {
  return 'sk-first-sub-discount-skip:' + String(code || '').toLowerCase();
}

function shouldSkip(code) {
  try {
    return sessionStorage.getItem(skipKey(code)) === '1';
  } catch (_) {
    return false;
  }
}

function markSkip(code) {
  try {
    sessionStorage.setItem(skipKey(code), '1');
  } catch (_) {
    /* ignore */
  }
}

function config() {
  const node = document.getElementById('sk-first-sub-discount-config');
  if (!node) return null;
  try {
    return JSON.parse(node.textContent || '{}');
  } catch (_) {
    return null;
  }
}

function cartHasSubscription(cart) {
  const items = cart && Array.isArray(cart.items) ? cart.items : [];
  return items.some((item) => item && item.selling_plan_allocation);
}

function existingCodes(cart) {
  const list = cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
  return list
    .map((entry) => (entry && entry.code ? String(entry.code) : ''))
    .filter(Boolean);
}

let inFlight = false;

async function syncDiscount() {
  const cfg = config();
  if (!cfg || !cfg.enabled || !cfg.code || shouldSkip(cfg.code) || inFlight) return;
  if (cfg.isFirstSubscription === false) return;

  const code = String(cfg.code).trim();
  if (!code) return;

  inFlight = true;
  try {
    const cartRes = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
    });
    if (!cartRes.ok) return;
    const cart = await cartRes.json();
    const codes = existingCodes(cart);
    const hasSub = cartHasSubscription(cart);
    const already = codes.some((c) => c.toLowerCase() === code.toLowerCase());

    if (!hasSub) {
      if (!already) return;
      const next = codes.filter((c) => c.toLowerCase() !== code.toLowerCase());
      await applyCodes(next, code, false);
      return;
    }

    if (already) return;
    await applyCodes([...codes, code], code, true);
  } catch (_) {
    /* ignore */
  } finally {
    inFlight = false;
  }
}

/**
 * @param {string[]} codes
 * @param {string} targetCode
 * @param {boolean} adding
 */
async function applyCodes(codes, targetCode, adding) {
  const updateUrl =
    (window.Theme && Theme.routes && Theme.routes.cart_update_url) || '/cart/update.js';
  const response = await fetch(
    updateUrl,
    fetchConfig('json', {
      body: JSON.stringify({
        discount: codes.join(','),
        sections: [CART_SECTION_ID],
      }),
    })
  );
  if (!response.ok) return;
  const data = await response.json();

  if (adding) {
    const applied = (data.discount_codes || []).find(
      (entry) =>
        entry &&
        String(entry.code).toLowerCase() === targetCode.toLowerCase() &&
        entry.applicable === true
    );
    if (!applied) {
      markSkip(targetCode);
      return;
    }
  }

  if (data.sections && data.sections[CART_SECTION_ID]) {
    morphSection(CART_SECTION_ID, data.sections[CART_SECTION_ID]);
  }
  document.dispatchEvent(new DiscountUpdateEvent(data, 'sk-first-sub-discount'));
}

function boot() {
  const cfg = config();
  if (!cfg || !cfg.enabled || !cfg.code) return;

  syncDiscount();
  document.addEventListener('cart:update', () => {
    window.requestAnimationFrame(() => syncDiscount());
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
